<script lang="ts">
  import Dropdown from "../../../Dropdown.svelte";
  import DatePicker from "../../../DateRange.svelte";
  import Button from "../../../Button.svelte";
  import Pagination from "../../../Pagination.svelte";
  import Table from "../../../Table.svelte";
  import { onMount } from "svelte";
  import { ComplianceService, type ComplianceFilters, type ComplianceRecord } from "../../../../lib/api/admin/complianceReport.service";
  import SearchInput from "../../../SearchInput.svelte";
  import Notification from "../../../Notification.svelte";

  // Metrics data with default values
  let metrics = {
    totalPayrollDeductions: 0,
    payrollGrowth: 0,
    employerContributions: 0,
    contributionsGrowth: 0,
    overallCompliance: 0,
    complianceGrowth: 0,
  };

  // State variables
  let reportData: ComplianceRecord[] = [];
  let loading = false;
  let error: string | null = null;
  let downloadingPdf = false;
  let downloadingCsv = false;
  let downloadError: string | null = null;

  // Notification state
  let showNotification = false;
  let notificationMessage = "";
  let notificationType: 'success' | 'error' = 'error';

  // Pagination
  let currentPage = 1;
  let totalPages = 1;
  let perPage = 10;
  let totalItems = 0;

  // Filter options
  let startDate = "";
  let endDate = "";
  let phrase = "";

  // Table columns configuration
  const columns = [
    { key: 'employer.name',
     label: 'Employer',
     render: (value: string, row: any) => {
      return `<span>${row.employer.name}</span>`;
     }
    },
    { 
      key: 'payroll_deductions', 
      label: 'Payroll Deductions',
      render: (value: number) => ComplianceService.formatCurrency(value)
    },
    { 
      key: 'employer_contributions', 
      label: 'Employer Contributions',
      render: (value: number) => ComplianceService.formatCurrency(value)
    },
    { 
      key: 'compliance_status', 
      label: 'Compliance Status',
      render: (value: string, row: any) => {
        return `
          <div class="status-badge ${ComplianceService.getComplianceStatusClass(value)}">
            ${getStatusIcon(value)}
            <span>${value}</span>
          </div>
        `;
  }
    }
  ];

  // Format percentage
  function formatPercentage(value: number): string {
    return value > 0 ? `+${value}%` : `${value}%`;
  }

  function getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case "compliant":
        return `
        <svg class="status-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
      `;
      case "non-compliant":
        return `
        <svg class="status-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
      `;
      case "at risk":
        return `
        <svg class="status-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      `;
      default:
        return "";
    }
  }

  async function loadComplianceData() {
    loading = true;
    error = null;
    
    try {
      // Prepare filters
      const filters: ComplianceFilters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (phrase) filters.phrase = phrase;

      // Load stats for metrics cards
      const statsResponse = await ComplianceService.getComplianceStats(filters);
      if (statsResponse.success && statsResponse.data) {
        metrics = {
          totalPayrollDeductions: statsResponse.data.total_payroll_deductions.value,
          payrollGrowth: statsResponse.data.total_payroll_deductions.change,
          employerContributions: statsResponse.data.employer_contributions.value,
          contributionsGrowth: statsResponse.data.employer_contributions.change,
          overallCompliance: statsResponse.data.overall_compliance.value,
          complianceGrowth: statsResponse.data.overall_compliance.change
        };
      }
      
      // Load compliance records for table
      const recordsResponse = await ComplianceService.getComplianceRecords(filters);
      if (recordsResponse.success && recordsResponse.data) {
        reportData = recordsResponse.data;
        // For pagination, we would typically get this from the API response
        // Since the API doesn't provide pagination info in the example, we'll simulate it
        totalItems = reportData.length;
        totalPages = Math.ceil(totalItems / perPage);
        // Slice the data for current page - in a real implementation, the API would handle pagination
        reportData = reportData.slice((currentPage - 1) * perPage, currentPage * perPage);
      } else {
        error = recordsResponse.message || "Failed to load compliance records";
        reportData = [];
      }
    } catch (err: any) {
      error = err.message || "An unexpected error occurred";
      reportData = [];
    } finally {
      loading = false;
    }
  }

  async function downloadAsPDF() {
    try {
      // Show loading indicator for PDF button only
      downloadingPdf = true;
      downloadError = null;
      
      // Call the service method to handle the download with current data
      await ComplianceService.downloadComplianceReportPDF(
        {
          start_date: startDate,
          end_date: endDate,
          phrase: phrase
        },
        reportData,
        metrics
      );
      
      // Show success notification
      notificationMessage = "PDF report downloaded successfully";
      notificationType = 'success';
      showNotification = true;
      
    } catch (err: any) {
      console.error("Failed to download PDF:", err);
      downloadError = "Failed to download PDF report";
      
      // Show error notification
      notificationMessage = "Failed to download PDF report";
      notificationType = 'error';
      showNotification = true;
    } finally {
      downloadingPdf = false;
    }
  }

  async function downloadAsCSV() {
    try {
      // Show loading indicator for CSV button only
      downloadingCsv = true;
      downloadError = null;
      
      // Call the service method to handle the download with current data
      await ComplianceService.downloadComplianceReportCSV(
        {
          start_date: startDate,
          end_date: endDate,
          phrase: phrase
        },
        reportData,
        metrics
      );
      
      // Show success notification
      notificationMessage = "CSV report downloaded successfully";
      notificationType = 'success';
      showNotification = true;
      
    } catch (err: any) {
      console.error("Failed to download CSV:", err);
      downloadError = "Failed to download CSV report";
      
      // Show error notification
      notificationMessage = "Failed to download CSV report";
      notificationType = 'error';
      showNotification = true;
    } finally {
      downloadingCsv = false;
    }
  }

  function handleNotificationDismiss() {
    showNotification = false;
    
    // If the notification was for a download error, reset the download error state
    if (notificationType === 'error' && (notificationMessage.includes('PDF') || notificationMessage.includes('CSV'))) {
      downloadError = null;
    }
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    loadComplianceData();
  }

  function handleFilterChange() {
    currentPage = 1; // Reset to first page when filters change
    loadComplianceData();
  }

  function handleSearchInput(event: CustomEvent) {
    phrase = event.detail.value;
  }

  onMount(() => {
    loadComplianceData();
  });
</script>

<!-- Add Notification component -->
<Notification 
  type={notificationType}
  message={notificationMessage}
  bind:show={showNotification}
  duration={5000}
  onDismiss={handleNotificationDismiss}
/>

<div class="payroll-report-container">
  <!-- Metrics Cards -->
  <div class="metrics-row">
    <div class="metric-card">
      <div class="metric-header">
        <span>Total Payroll Deductions</span>
        <div class="metric-icon payroll-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="16"
            viewBox="0 0 15 16"
            fill="none"
          >
            <rect
              y="4.70508"
              width="2.82353"
              height="11.2941"
              rx="1.41176"
              fill="white"
            />
            <rect
              x="5.64648"
              width="2.82353"
              height="16"
              rx="1.41176"
              fill="white"
            />
            <rect
              x="11.2939"
              y="9.41211"
              width="2.82353"
              height="6.58824"
              rx="1.41176"
              fill="white"
            />
          </svg>
        </div>
      </div>
      <div class="metric-value">
        {ComplianceService.formatCurrency(metrics.totalPayrollDeductions)}
      </div>
      <div class="metric-growth {metrics.payrollGrowth >= 0 ? 'positive' : 'negative'}">
        {formatPercentage(metrics.payrollGrowth)}
      </div>
    </div>

    <div class="metric-card">
      <div class="metric-header">
        <span>Employer Contributions</span>
        <div class="metric-icon contributions-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="11"
            viewBox="0 0 18 11"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12.2139 0.642857C12.2139 0.997894 12.5017 1.28571 12.8567 1.28571H16.7139V5.14286C16.7139 5.4979 17.0017 5.78572 17.3567 5.78572C17.7118 5.78572 17.9996 5.4979 17.9996 5.14286V0.642857C17.9996 0.600647 17.9955 0.558842 17.9872 0.517442C17.979 0.476042 17.9668 0.435843 17.9506 0.396846C17.9345 0.357849 17.9147 0.320802 17.8912 0.285705C17.8678 0.250608 17.8411 0.218136 17.8113 0.188289C17.7814 0.158442 17.749 0.131792 17.7139 0.108341C17.6788 0.0848901 17.6417 0.065088 17.6027 0.0489343C17.5637 0.0327814 17.5235 0.0205873 17.4821 0.0123518C17.4407 0.00411727 17.3989 0 17.3567 0H12.8567C12.5017 0 12.2139 0.28782 12.2139 0.642857Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M17.1689 0.830866C16.9178 0.579816 16.5108 0.579816 16.2597 0.830866L10.7403 6.35025C10.6148 6.47581 10.4633 6.53859 10.2857 6.53859C10.1082 6.53859 9.95661 6.47579 9.83105 6.3502L7.79243 4.31158C7.41584 3.9349 6.96122 3.74656 6.42857 3.74656C5.89593 3.74656 5.44132 3.93488 5.06476 4.31153L0.188287 9.18801C-0.0627625 9.43906 -0.0627625 9.84609 0.188287 10.0971C0.439336 10.3482 0.846375 10.3482 1.09742 10.0971L5.9739 5.22067C6.09947 5.09507 6.25102 5.03228 6.42857 5.03228C6.60612 5.03228 6.75766 5.09506 6.88319 5.22062L8.92191 7.25934C9.29847 7.63598 9.75307 7.82431 10.2857 7.82431C10.8184 7.82431 11.273 7.63597 11.6496 7.25929L17.1689 1.74C17.4199 1.48895 17.4199 1.08191 17.1689 0.830866Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
      <div class="metric-value">
        {ComplianceService.formatCurrency(metrics.employerContributions)}
      </div>
      <div class="metric-growth {metrics.contributionsGrowth >= 0 ? 'positive' : 'negative'}">
        {formatPercentage(metrics.contributionsGrowth)}
      </div>
    </div>

    <div class="metric-card">
      <div class="metric-header">
        <span>Overall Compliance</span>
        <div class="metric-icon compliance-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.8553 4.23333C10.8553 6.58338 8.93227 8.46666 6.53265 8.46666C4.1339 8.46666 2.21003 6.58338 2.21003 4.23333C2.21003 1.88328 4.1339 0 6.53265 0C8.93227 0 10.8553 1.88328 10.8553 4.23333ZM0 13.2598C0 11.0844 3.00931 10.5401 6.53263 10.5401C10.0751 10.5401 13.0653 11.1031 13.0653 13.2803C13.0653 15.4557 10.056 16 6.53263 16C2.99017 16 0 15.437 0 13.2598ZM12.5987 4.31049C12.5987 5.50724 12.2317 6.62392 11.5881 7.5515C11.5211 7.6469 11.5802 7.77552 11.6968 7.79596C11.8585 7.82237 12.0247 7.83855 12.1943 7.84196C13.8816 7.8854 15.3958 6.82153 15.8141 5.21934C16.4343 2.84203 14.6148 0.707478 12.2969 0.707478C12.0455 0.707478 11.8046 0.733031 11.5698 0.779879C11.5376 0.786693 11.5028 0.802025 11.4854 0.829282C11.4628 0.864204 11.4793 0.909349 11.502 0.939161C12.1986 1.89571 12.5987 3.06008 12.5987 4.31049ZM15.3932 9.51366C16.5273 9.73087 17.2727 10.1729 17.5814 10.8177C17.8432 11.3458 17.8432 11.9591 17.5814 12.4872C17.1092 13.4855 15.5854 13.8066 14.9931 13.8892C14.8704 13.9063 14.7722 13.8041 14.7852 13.6831C15.0879 10.9165 12.6813 9.6048 12.0586 9.30327C12.0325 9.28879 12.0264 9.26835 12.029 9.25472C12.0307 9.24621 12.042 9.23258 12.062 9.23002C13.4093 9.20447 14.8583 9.3859 15.3932 9.51366Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
      <div class="metric-value">{ComplianceService.formatPercentage(metrics.overallCompliance)}</div>
      <div class="metric-growth {metrics.complianceGrowth >= 0 ? 'positive' : 'negative'}">
        {formatPercentage(metrics.complianceGrowth)}
      </div>
    </div>
  </div>

  <div class="actions">
    <Button variant="secondary" on:click={downloadAsPDF} disabled={downloadingPdf}>
      {downloadingPdf ? "Downloading..." : "Download as PDF"}
    </Button>
    <Button variant="secondary" on:click={downloadAsCSV} disabled={downloadingCsv}>
      {downloadingCsv ? "Downloading..." : "Download as CSV"}
    </Button>
  </div>

  <!-- Filters and Actions Row -->
  <div class="filters-row">
    <div class="filters">
      <div class="filter-item">
        <SearchInput 
          placeholder="Search employers..." 
          bind:value={phrase} 
          on:input={handleSearchInput}
          width="250px"
        />
      </div>
      <div class="filter-item date-picker">
        <DatePicker 
          placeholder="Date Range"
          bind:startDate bind:endDate
        />
      </div>
      <div class="filter-item">
        <Button variant="primary" on:click={handleFilterChange}>
          Apply Filters
        </Button>
      </div>
    </div>
   
  </div>

  {#if loading}
    <div class="loading-container">
      <div class="spinner"></div>
      <p>Loading compliance data...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <div class="button-wrapper-payroll-compliance-report">    
      <Button variant="primary" on:click={loadComplianceData}>Try Again</Button>
  </div>
              </div>
  {:else if reportData.length === 0}
    <div class="empty-state">
      <p>No compliance records found for the selected filters.</p>
      <Button variant="primary" on:click={() => {
        startDate = "";
        endDate = "";
        loadComplianceData();
      }}>Reset Filters</Button>
  </div>
  {:else}
    <!-- Table using Table component -->
    <Table 
      title="Payroll Compliance Report"
      columns={columns}
      data={reportData}
    />

  <!-- Pagination -->
  <div class="pagination-wrapper">
      <Pagination 
        currentPage={currentPage} 
        totalPages={totalPages} 
        on:pageChange={handlePageChange} 
      />
  </div>
  {/if}
</div>

<style>
  .payroll-report-container {
    padding: 20px;
    background-color: #f9fafb;
    border-radius: 8px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      Helvetica, Arial, sans-serif;
  }

  .metrics-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 24px;
  }

  .metric-card {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .metric-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #6b7280;
    font-size: 14px;
    margin-bottom: 8px;
  }

  .metric-icon {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  .metric-icon svg {
    width: 18px;
    height: 18px;
  }

  .payroll-icon {
    background-color: #2A9D8F;
  }

  .contributions-icon {
    background-color: #2A9D8F;
  }

  .compliance-icon {
    background-color: #2A9D8F;
  }

  .metric-value {
    font-size: 24px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
  }

  .metric-growth {
    font-size: 14px;
    font-weight: 500;
  }

  .positive {
    color: #2A9D8F;
  }

  .negative {
    color: #ef4444;
  }

  .filters-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
  }

  .filters {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .filter-item {
    min-width: 180px;
  }

  .date-picker {
    min-width: 300px;
  }

  .actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-bottom: 10px;
  }

  /* Button styling is now handled by the Button component */
  :global(.actions button) {
    border-radius: 25rem !important;
    width: fit-content !important;
  }

  .button-wrapper-payroll-compliance-report {
    display: flex;
    justify-content: flex-end;
    width: fit-content !important;
  } 

  /* Apply styles to badges rendered by the Table component */
  :global(.status-badge.status-compliant) {
    background-color: #d1fae5 !important;
    color: #065f46 !important;
  }

  :global(.status-badge.status-non-compliant) {
    background-color: #fee2e2 !important;
    color: #b91c1c !important;
  }

  :global(.status-badge.status-at-risk) {
    background-color: #fef3c7 !important;
    color: #92400e !important;
  }

  :global(.status-icon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
  }

  :global(.status-compliant .status-icon) {
    color: #10b981 !important;
  }

  :global(.status-non-compliant .status-icon) {
    color: #ef4444 !important;
  }

  :global(.status-at-risk .status-icon) {
    color: #f59e0b !important;
  }

  /* Apply icon styles for badges rendered by the Table component */
  :global(.status-badge.status-compliant .status-icon) {
    color: #10b981 !important;
  }

  :global(.status-badge.status-non-compliant .status-icon) {
    color: #ef4444 !important;
  }

  :global(.status-badge.status-at-risk .status-icon) {
    color: #f59e0b !important;
  }

  .pagination-wrapper {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top-color: #2A9D8F;
    animation: spin 1s ease-in-out infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .error-container {
    background-color: #fee2e2;
    border: 1px solid #ef4444;
    border-radius: 8px;
    padding: 16px;
    margin: 20px 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .error-message {
    color: #b91c1c;
    margin-bottom: 16px;
  }

  .empty-state {
    background-color: #f3f4f6;
    border-radius: 8px;
    padding: 40px;
    margin: 20px 0;
    text-align: center;
  }

  .empty-state p {
    color: #6b7280;
    margin-bottom: 16px;
  }

  @media (max-width: 992px) {
    .metrics-row {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .metrics-row {
      grid-template-columns: 1fr;
    }

    .filters-row {
      flex-direction: column;
      align-items: stretch;
    }

    .filters {
      width: 100%;
    }

    .filter-item {
      width: 100%;
      min-width: unset;
    }

    .actions {
      width: 100%;
      justify-content: space-between;
    }

    :global(.actions button) {
      flex: 1;
      text-align: center;
    }
  }
</style>
